import * as d3 from 'd3';
import * as cloud from 'd3-cloud';

const WORD_CLOUD_SCALE = 0.18; // this controls the size of the whole word cloud
const WORD_SIZE_MULTIPLIER = 0.5; // this dampens the difference between word sizes
const ASPECT_RATIO = 2 / 3;

interface FormattedWord {
  text: string;
  size: number;
}

export default class WordCloud {
  container: any;
  svg: any;
  data: any;
  constructor(container: any, svg: any, data: any[]) {
    this.container = container;
    this.svg = svg;
    this.data = data;
  }

  createTopWordsChart = () => {
    if (this.noData()) {
      return;
    }

    const formattedWords = this.getFormattedWords();

    const layout = cloud()
      .size([this.getContainerWidth(), this.getContainerHeight()])
      .words(formattedWords)
      .padding(15)
      .rotate(() => {
        return ~~(Math.random() * 2) * 90;
      })
      .fontSize((word: FormattedWord) => {
        return word.size;
      })
      .on('end', () => { this.refreshChart(formattedWords); });
    layout.start();
  }

  refreshChart = (formattedWords: FormattedWord[]) => {
    d3.select(this.svg)
      .attr('width', this.getContainerWidth())
      .attr('height', this.getContainerHeight())
      .append('g')
      .attr('transform', `translate(${this.getContainerWidth() / 2},${this.getContainerHeight() / 2})`)
      .selectAll('text')
      .data(formattedWords);

    const getFill = d3.scaleOrdinal(d3.schemeCategory10);
    const svg = d3.select('.word-cloud svg g');
    const wordCloud = svg.selectAll('g text').data(formattedWords, (word: any) => { return word.text; });

    wordCloud.enter()
      .append('text')
      .style('fill', (_: any, index: number) => {
        return getFill(index);
      })
      .style('font-size', (word: any) => { return `${word.size}px`; })
      .attr('text-anchor', 'middle')
      .attr('font-size', 1)
      .attr('transform', (word: any) => {
        return `translate(${[word.x, word.y]})rotate(${word.rotate})`;
      })
      .text((word: any) => {
        return word.text;
      });

    wordCloud.transition()
      .duration(1000)
      .attr('transform', (word: any) => { return `translate(${[word.x, word.y]})rotate(${word.rotate})`; })
      .style('fill-opacity', 1);

    wordCloud.exit()
      .transition()
      .duration(200)
      .style('fill-opacity', 1e-6)
      .attr('font-size', 1)
      .remove();
  }

  getFormattedWords = (): FormattedWord[] => {
    return this.data.map((word: any) => {
      return {
        text: this.getWordText(word),
        size: this.getWordSize(word),
      };
    });
  }

  noData = () => {
    return this.data.length === 0;
  }

  getWordText = (word: any) => {
    return word[0];
  }

  getWordSize = (word: any) => {
    return this.getAverageFrequency()
           + (this.getWordFrequency(word) - this.getAverageFrequency()) * WORD_SIZE_MULTIPLIER;
  }

  getContainerWidth = () => {
    return this.container.offsetWidth;
  }

  getContainerHeight = () => {
    return ASPECT_RATIO * this.getContainerWidth();
  }

  getAverageWordFrequency = () => {
    return this.average(this.getWordFrequencies());
  }

  getWordFrequency = (word: any) => {
    return this.wordFrequency(word) * this.wordSizeScalingFactor();
  }

  wordSizeScalingFactor = () => {
    return this.getContainerWidth() / this.getFrequencyOfLargestWord() * WORD_CLOUD_SCALE;
  }

  getFrequencyOfLargestWord = () => {
    return this.data[0][1];
  }

  getWordFrequencies = () => {
    return this.data.map((word: any) => {
      return this.wordFrequency(word);
    });
  }

  getAverageFrequency = () => {
    return this.getAverageWordFrequency() * this.wordSizeScalingFactor();
  }

  wordFrequency = (word: any) => {
    return word[1];
  }

  average = (arr: number[]) => {
    return arr.reduce((a, b) => { return a + b; }, 0) / arr.length;
  }
}
